import * as React from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { CircularProgress, TableHead } from '@mui/material'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import ConfirmDelete from '../../../../components/dialogs/ConfirmDelete'
import ResetTemporaryPassword from '../../../../components/dialogs/ResetTemporaryPassword'
import ArrowRightIcon from '../../../../assets/icons/Arrows-chevron/Arrow-Right.svg'
import { capitalize } from '../../../../utils/functions'
import { NewStudentState } from '../../../../features/types'
import EditStudentDialog from './EditStudentDialog'
import dollarLogo from '../../../../assets/icons/Dollar.svg'
import SendWelcomeMessageDialog from '../../../../components/dialogs/SendWelcomeMessageDialog'

interface Props {
    studentsData: NewStudentState[]
    isFetching: boolean
    isLoadingUsersInfo: boolean
    onResetPassword?: (id: number) => void
    onDelete: (studentId: number) => void
    onSendWelcomeMessage?: (id: number) => void
}

const StudentsTable: React.FC<Props> = ({
    studentsData = [],
    isFetching,
    isLoadingUsersInfo,
    onResetPassword,
    onDelete,
    onSendWelcomeMessage,
}) => {
    const { formatMessage, locale } = useIntl()

    const getGroupOfUser = (id: number): string => {
        const user = studentsData.find((user) => user.id === id)

        if (user) {
            return user.groupTitles
        }
    }

    const getDateOfUser = (id: number): string => {
        const user = studentsData.find((user) => user.id === id)

        if (user) {
            return user.maxEndDate
        }
    }

    const getCourseOfUser = (id: number): string => {
        const user = studentsData.find((user) => user.id === id)

        if (user) {
            return user.coursesTitleKz
        }
    }


    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell>#</TableCell>

                            <TableCell>
                                {formatMessage({ id: 'fullName' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'phone' })}
                            </TableCell>

                            <TableCell>
                                Адрес
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'temporaryPassword' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'group' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'startDate' })} -{' '}
                                {formatMessage({ id: 'endDate' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'courses' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'paid' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'send_message' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'edit' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'details' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'delete' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    {studentsData.length ? (
                        <TableBody>
                            {studentsData.map((row, index) => (
                                <TableRow key={row.id}>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {index + 1}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.firstName} {row?.lastName}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.mobile}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.address}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        <ResetTemporaryPassword
                                            password={row?.password}
                                            fullName={
                                                row?.lastName +
                                                ' ' +
                                                row?.firstName
                                            }
                                            isLoading={isFetching}
                                            confirmAction={() =>
                                                onResetPassword(
                                                    row?.id as number,
                                                )
                                            }
                                        />
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {isLoadingUsersInfo ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            getGroupOfUser(row?.id)
                                        )}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {isLoadingUsersInfo ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            getDateOfUser(row?.id)
                                        )}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {isLoadingUsersInfo ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            getCourseOfUser(row?.id)
                                        )}
                                    </TableCell>

                                    <TableCell
                                        align="center"
                                    >
                                        {isLoadingUsersInfo ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            row?.paid ?
                                                <img
                                                    alt="logo"
                                                    src={dollarLogo}
                                                /> : ''
                                        )}

                                    </TableCell>

                                    <TableCell
                                        style={{ width: 60 }}
                                        align="right"
                                    >
                                        <SendWelcomeMessageDialog
                                            confirmAction={() =>
                                                onSendWelcomeMessage(
                                                    row?.id as number
                                                )}
                                        />
                                    </TableCell>

                                    <TableCell
                                        style={{ width: 60 }}
                                        align="right"
                                    >
                                        <EditStudentDialog editStudent={row} />
                                    </TableCell>

                                    <TableCell
                                        style={{ width: 60 }}
                                        align="right"
                                    >
                                        <Link
                                            to={`${row?.id}?fullName=${row?.firstName +
                                                ' ' +
                                                row?.lastName
                                                }`}
                                        >
                                            <IconButton
                                                sx={{ background: '#f3f4f5' }}
                                            >
                                                <img
                                                    src={ArrowRightIcon}
                                                    alt="edit"
                                                />
                                            </IconButton>
                                        </Link>
                                    </TableCell>

                                    <TableCell
                                        style={{ width: 60 }}
                                        align="right"
                                    >
                                        <ConfirmDelete
                                            confirmAction={() =>
                                                onDelete(row?.id as number)
                                            }
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    ) : (
                        !isFetching && (
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                        colSpan={12}
                                    >
                                        {formatMessage({ id: 'noData' })}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )
                    )}

                </Table>
            </TableContainer>
        </Box>
    )
}

export default StudentsTable
